import React, { useState } from 'react';
import {
  View, Text, TextInput, TouchableOpacity,
  StyleSheet, Switch, FlatList, Alert,
} from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import type { RunStackParamList } from '../../navigation/AppNavigator';
import { useLandmarksStore, usePresetRunsStore } from '../../store';
import { presetRunsApi } from '../../api';
import type { Landmark } from '../../types';

type Props = NativeStackScreenProps<RunStackParamList, 'CreatePresetRun'>;

export default function CreatePresetRunScreen({ navigation }: Props) {
  const { landmarks } = useLandmarksStore();
  const upsertPresetRun = usePresetRunsStore((s) => s.upsertPresetRun);
  const [name, setName] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [ordered, setOrdered] = useState<Landmark[]>([]);

  function addLandmark(lm: Landmark) {
    setOrdered([...ordered, lm]);
  }

  function removeLandmark(idx: number) {
    setOrdered(ordered.filter((_, i) => i !== idx));
  }

  function moveUp(idx: number) {
    if (idx === 0) return;
    const next = [...ordered];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    setOrdered(next);
  }

  function moveDown(idx: number) {
    if (idx === ordered.length - 1) return;
    const next = [...ordered];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    setOrdered(next);
  }

  async function handleSave() {
    if (!name.trim()) { Alert.alert('Error', 'Please enter a run name.'); return; }
    if (ordered.length < 2) { Alert.alert('Error', 'Select at least 2 landmarks.'); return; }
    try {
      const created = await presetRunsApi.create({
        name: name.trim(),
        isPublic,
        landmarkIds: ordered.map((l) => l.landmarkId),
      });
      upsertPresetRun(created);
      navigation.goBack();
    } catch (e: any) {
      Alert.alert('Failed to save', e.message ?? 'Unknown error');
    }
  }

  return (
    <View style={styles.container}>
      <TextInput style={styles.input} placeholder="Run Name (e.g. Morning 5K)" value={name} onChangeText={setName} />
      <View style={styles.row}>
        <Text>Share publicly</Text>
        <Switch value={isPublic} onValueChange={setIsPublic} />
      </View>

      {ordered.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionLabel}>Run Order ({ordered.length} landmarks)</Text>
          {ordered.map((lm, idx) => (
            <View key={idx} style={styles.orderedItem}>
              <Text style={styles.orderedNum}>{idx + 1}</Text>
              <Text style={styles.orderedName}>{lm.name}</Text>
              <TouchableOpacity onPress={() => moveUp(idx)}><Text style={styles.arrow}>▲</Text></TouchableOpacity>
              <TouchableOpacity onPress={() => moveDown(idx)}><Text style={styles.arrow}>▼</Text></TouchableOpacity>
              <TouchableOpacity onPress={() => removeLandmark(idx)}><Text style={styles.remove}>✕</Text></TouchableOpacity>
            </View>
          ))}
        </View>
      )}

      <Text style={styles.sectionLabel}>Add Landmarks</Text>
      <FlatList
        data={landmarks}
        keyExtractor={(i) => i.landmarkId}
        ListEmptyComponent={<Text style={styles.empty}>No landmarks yet. Create some from the Map tab.</Text>}
        renderItem={({ item }) => {
          const timesAdded = ordered.filter((o) => o.landmarkId === item.landmarkId).length;
          return (
            <TouchableOpacity style={styles.item} onPress={() => addLandmark(item)}>
              <Text style={styles.itemName}>
                {item.name}
                {timesAdded > 0 && <Text style={styles.addedCount}> (added {timesAdded}×)</Text>}
              </Text>
              <Text style={styles.add}>+ Add</Text>
            </TouchableOpacity>
          );
        }}
        ItemSeparatorComponent={() => <View style={styles.sep} />}
        style={{ flex: 1 }}
      />

      <TouchableOpacity style={styles.button} onPress={handleSave}>
        <Text style={styles.buttonText}>Save Preset Run</Text>
      </TouchableOpacity>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#fff', padding: 16 },
  input: { borderWidth: 1, borderColor: '#ccc', borderRadius: 8, padding: 12, fontSize: 16, marginBottom: 12 },
  row: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  section: { marginBottom: 12 },
  sectionLabel: { fontWeight: '700', fontSize: 14, marginBottom: 8, color: '#333' },
  orderedItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: 6, gap: 8 },
  orderedNum: { width: 24, height: 24, borderRadius: 12, backgroundColor: '#1a73e8', color: '#fff', textAlign: 'center', lineHeight: 24, fontWeight: '700', fontSize: 13 },
  orderedName: { flex: 1, fontSize: 14 },
  arrow: { fontSize: 16, color: '#555', paddingHorizontal: 4 },
  remove: { color: '#ea4335', fontWeight: '700', paddingHorizontal: 4 },
  item: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: 12 },
  itemName: { fontSize: 15, flex: 1 },
  addedCount: { color: '#1a73e8', fontSize: 13 },
  add: { color: '#1a73e8', fontWeight: '600' },
  sep: { height: 1, backgroundColor: '#f0f0f0' },
  empty: { textAlign: 'center', color: '#aaa', padding: 16 },
  button: { backgroundColor: '#1a73e8', borderRadius: 8, padding: 14, alignItems: 'center', marginTop: 8 },
  buttonText: { color: '#fff', fontWeight: '600', fontSize: 15 },
});
